import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { placeMenu } from "@/components/menu/menuPlacement";
import { useSessionState } from "@/lib/sessionState";
import {
  filterParam,
  filtersOf,
  optionsOf,
  rangeOf,
  rangeValue,
  type ColumnFilterDef,
  type FilterOption,
  type FilterValues,
} from "@/lib/columnFilters";

// The filter on one column of a list (Camiel, 21-09-2026: "zodat er een filtericoontje bij
// komt als je de muis op de kolom zet, zodat je op die ene kolom specifiek kunt filteren").
// The icon shows on a header under the pointer and stays once the column is filtered; it
// opens a small window with what fits the column — text, a choice, days or numbers. What
// is filtered stands above the table, each with its own cross. The rules are
// lib/columnFilters.ts; a paged list sends them to the API, a list held in the browser
// tests its own rows.

/** The column filters of a list and how to change them. */
export interface ListFilters {
  values: FilterValues;
  set: (key: string, value: string) => void;
  clear: () => void;
}

/** Filters in the address bar, like the sort: a back link or a shared link keeps them. */
export function useUrlListFilters(search: object, updateParams: (updates: Record<string, string>) => void): ListFilters {
  const values = filtersOf(search);
  return {
    values,
    set: (key, value) => updateParams({ [filterParam(key)]: value, cursor: "" }),
    clear: () => updateParams({ ...Object.fromEntries(Object.keys(values).map((key) => [filterParam(key), ""])), cursor: "" }),
  };
}

/**
 * Filters of a list that is loaded whole: they live in the screen, and with a `memory` key in this tab while the
 * screen is gone (lib/sessionState).
 */
export function useLocalListFilters(memory: string | null = null): ListFilters {
  const [values, setValues] = useSessionState<Record<string, string>>(memory, {});
  return {
    values,
    set: (key, value) =>
      setValues((current) => {
        const next = { ...current };
        if (value.trim()) next[key] = value;
        else delete next[key];
        return next;
      }),
    clear: () => setValues({}),
  };
}

function FunnelIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
    </svg>
  );
}

function optionLabel(option: FilterOption, t: (key: never) => string): string {
  return option.label ?? (option.labelKey ? t(option.labelKey as never) : option.value);
}

/** 2026-09-21 as 21-09-2026, as dates read in Willems's screens. */
function dayText(day: string): string {
  const [y, m, d] = day.split("-");
  return y && m && d ? `${d}-${m}-${y}` : day;
}

/** What a filter says, in words, for its chip above the table. */
export function useFilterText() {
  const { t } = useTranslation();
  return (def: ColumnFilterDef, value: string): string => {
    switch (def.kind) {
      case "text":
        return `“${value}”`;
      case "options":
        return optionsOf(value)
          .map((chosen) => {
            const option = def.options.find((candidate) => candidate.value === chosen);
            return option ? optionLabel(option, t) : chosen;
          })
          .join(", ");
      case "date":
      case "number": {
        const [from, to] = rangeOf(value);
        const unit = def.kind === "number" && def.unitKey ? ` ${t(def.unitKey as never)}` : "";
        const show = def.kind === "date" ? dayText : (text: string) => `${text}${unit}`;
        if (from && to) return from === to ? show(from) : `${show(from)} – ${show(to)}`;
        if (from) return t("columns.filterFrom", { value: show(from) });
        return t("columns.filterUntil", { value: show(to) });
      }
    }
  };
}

/** The icon in a header cell, and the window it opens. */
export function ColumnFilterButton({
  columnKey,
  label,
  def,
  filters,
}: {
  columnKey: string;
  label: string;
  def: ColumnFilterDef;
  filters: ListFilters;
}) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const value = filters.values[columnKey] ?? "";
  const active = value !== "";

  const close = () => {
    setAnchor(null);
    buttonRef.current?.focus();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`wl-th-filter${active ? " is-active" : ""}${anchor ? " is-open" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={anchor !== null}
        aria-label={t("columns.filterBy", { column: label })}
        title={active ? t("columns.filteredBy", { column: label }) : t("columns.filterBy", { column: label })}
        draggable={false}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (anchor) {
            setAnchor(null);
            return;
          }
          setAnchor((e.currentTarget.closest("th") ?? e.currentTarget).getBoundingClientRect());
        }}
      >
        <FunnelIcon />
      </button>
      {anchor && (
        <FilterWindow anchor={anchor} label={label} onClose={close} buttonRef={buttonRef}>
          <FilterEditor def={def} value={value} onChange={(next) => filters.set(columnKey, next)} onDone={close} />
        </FilterWindow>
      )}
    </>
  );
}

function FilterWindow({
  anchor,
  label,
  onClose,
  buttonRef,
  children,
}: {
  anchor: DOMRect;
  label: string;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);

  // Under the header, left-aligned with it; pushed back when it would run off screen —
  // before the first paint, so it never shows in the wrong place. It is not hidden until
  // then: a field that is hidden cannot take the focus it opens with.
  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    setPlace(
      placeMenu({
        x: anchor.left,
        y: anchor.bottom + 4,
        width: box.width,
        height: box.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      }),
    );
  }, [anchor]);

  useEffect(() => {
    const inside = (target: EventTarget | null) =>
      target instanceof Node && (ref.current?.contains(target) || buttonRef.current?.contains(target));
    const onPointerDown = (e: PointerEvent) => {
      if (!inside(e.target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    // A page that scrolls away from under it takes the header with it.
    const onScroll = (e: Event) => {
      if (!(e.target instanceof Node && ref.current?.contains(e.target))) onClose();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose, buttonRef]);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={t("columns.filterBy", { column: label })}
      className="wl-popover wl-col-filter"
      style={{
        position: "fixed",
        left: place?.left ?? anchor.left,
        top: place?.top ?? anchor.bottom + 4,
      }}
    >
      <div className="wl-col-filter__title">{t("columns.filterBy", { column: label })}</div>
      {children}
    </div>,
    document.body,
  );
}

function FilterEditor({
  def,
  value,
  onChange,
  onDone,
}: {
  def: ColumnFilterDef;
  value: string;
  onChange: (next: string) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const clear = (
    <div className="wl-col-filter__foot">
      <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" disabled={value === ""} onClick={() => onChange("")}>
        {t("columns.filterClear")}
      </button>
      <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" onClick={onDone}>
        {t("columns.filterDone")}
      </button>
    </div>
  );

  switch (def.kind) {
    case "text":
      return (
        <>
          <TextFilter value={value} onChange={onChange} onDone={onDone} />
          {clear}
        </>
      );
    case "options":
      return (
        <>
          <OptionsFilter options={def.options} value={value} onChange={onChange} />
          {clear}
        </>
      );
    case "date":
    case "number":
      return (
        <>
          <RangeFilter
            kind={def.kind}
            unitKey={def.kind === "number" ? def.unitKey : undefined}
            value={value}
            onChange={onChange}
            onDone={onDone}
          />
          {clear}
        </>
      );
  }
}

/** Typing filters as you go: a moment after the last key, not on every key. */
function useSettled(value: string, onChange: (next: string) => void, delay = 300) {
  const [draft, setDraft] = useState(value);
  const latest = useRef(onChange);
  latest.current = onChange;
  // Cleared from elsewhere — the chip's cross — while the window is open.
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => latest.current(draft), delay);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return [draft, setDraft] as const;
}

function TextFilter({ value, onChange, onDone }: { value: string; onChange: (next: string) => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useSettled(value, onChange);
  return (
    <input
      className="wl-input"
      autoFocus
      value={draft}
      maxLength={200}
      placeholder={t("columns.filterContains")}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onChange(draft);
          onDone();
        }
      }}
    />
  );
}

function OptionsFilter({
  options,
  value,
  onChange,
}: {
  options: readonly FilterOption[];
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  const chosen = optionsOf(value);
  const toggle = (option: string) => {
    const next = chosen.includes(option) ? chosen.filter((other) => other !== option) : [...chosen, option];
    // In the order of the list, so the same choice always reads the same.
    onChange(
      options
        .map((candidate) => candidate.value)
        .filter((candidate) => next.includes(candidate))
        .join(","),
    );
  };
  return (
    <div className="wl-col-filter__options">
      {options.map((option, index) => (
        <label key={option.value} className="wl-col-filter__option">
          <input type="checkbox" autoFocus={index === 0} checked={chosen.includes(option.value)} onChange={() => toggle(option.value)} />
          <span>{optionLabel(option, t)}</span>
        </label>
      ))}
    </div>
  );
}

function RangeFilter({
  kind,
  unitKey,
  value,
  onChange,
  onDone,
}: {
  kind: "date" | "number";
  unitKey?: string;
  value: string;
  onChange: (next: string) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  // A date is done when the browser says so; a number waits for the typing to stop.
  const [draft, setDraft] = useSettled(value, onChange, kind === "date" ? 0 : 400);
  // One value reads as both sides; typing only one side makes it an open range.
  const [draftFrom, draftTo] = rangeOf(draft);
  const input = (side: "from" | "to") => (
    <label className="wl-col-filter__side">
      <span className="t-caption t-muted">
        {t(side === "from" ? "columns.filterRangeFrom" : "columns.filterRangeTo")}
        {unitKey && ` (${t(unitKey as never)})`}
      </span>
      <input
        className="wl-input"
        autoFocus={side === "from"}
        type={kind === "date" ? "date" : "text"}
        inputMode={kind === "number" ? "decimal" : undefined}
        value={side === "from" ? draftFrom : draftTo}
        onChange={(e) => setDraft(side === "from" ? rangeValue(e.target.value, draftTo) : rangeValue(draftFrom, e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onChange(draft);
            onDone();
          }
        }}
      />
    </label>
  );
  return (
    <div className="wl-col-filter__range">
      {input("from")}
      {input("to")}
    </div>
  );
}

/** What is filtered, above the table: one chip per column, and everything off at once. */
export function ActiveFilters<C extends { labelKey: string; filter?: ColumnFilterDef }>({
  columns,
  keyOf,
  filters,
}: {
  columns: readonly C[];
  keyOf: (column: C) => string;
  filters: ListFilters;
}) {
  const { t } = useTranslation();
  const text = useFilterText();
  const shown = columns.filter((column) => column.filter && filters.values[keyOf(column)]);
  if (shown.length === 0) return null;
  return (
    <div className="wl-col-filter-bar" role="status">
      <span className="t-caption t-muted">{t("columns.filteredOn")}</span>
      {shown.map((column) => {
        const key = keyOf(column);
        const label = t(column.labelKey as never);
        return (
          <span key={key} className="wl-chip wl-chip--navy wl-col-filter-bar__chip">
            <span>
              {label}: {text(column.filter!, filters.values[key]!)}
            </span>
            <button
              type="button"
              className="wl-col-filter-bar__remove"
              aria-label={t("columns.filterRemove", { column: label })}
              onClick={() => filters.set(key, "")}
            >
              ×
            </button>
          </span>
        );
      })}
      {shown.length > 1 && (
        <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={filters.clear}>
          {t("columns.filterClearAll")}
        </button>
      )}
    </div>
  );
}
